import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { apiConstants, postGeneric } from '../../../common/constants';
import { getMerchant } from '../../../helpers/ReduxHelpers';
import {
    BUCKET_KEYS,
    PILL_KEYS,
    buildBaselineAndFilter,
    buildBaselineOrFilter,
    buildPillFilter,
    fmt,
} from './constants';

const EMPTY_STATE = {
    buckets: {},
    summary: {},
    perf: {},
    reasons: [],
    parties: { data: [], total: 0, page: 0, size: 0 },
    total: 0,
    active: 0,
    miss1: 0,
    miss2: 0,
    miss3plus: 0,
    needsScheduling: 0,
    delivered: 0,
    prozoFault: 0,
    nonProzo: 0,
    eventuallyDelivered: 0,
    prozoFaultPending: 0,
    abdActiveOpen: 0,
    avgDaysPending: 0,
    resolvedToday: 0,
    totalAppts: 0,
    firstAttemptRate: 0,
    avgAttempts: 0,
    prozoMissRate: 0,
    abdLast3m: 0,
};

const stableSortedKey = (arr) => (arr || []).slice().sort().join(',');

export default function useAppointmentDashboard({
    filters = {},
    include = ['buckets', 'summary'],
    groupBy = [],
    extraBaseline = [],
    partiesPage = 0,
    partiesSize = 50,
    partiesSearch = '',
    partiesAbdFilter = '',
    reload = 0,
    appointmentRequired = true,
    // Optional [{ name, andfilter }] counted instead of the standard pills (same baseline).
    buckets: customBuckets,
}) {
    const merchant = getMerchant();
    const merchantId = merchant?.id || '';

    const [data, setData] = useState(EMPTY_STATE);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const reqIdRef = useRef(0);

    const from = fmt(filters.fromDate);
    const to = fmt(filters.toDate);
    const courierProzoId = filters.courier || '';
    const city = filters.city || '';
    const state = filters.state || '';
    const awb = filters.awb || '';
    const clientOrderId = filters.clientOrderId || '';
    const missCategory = filters.missCategory || '';
    const documents = filters.documents || '';
    const attempts = filters.attempts || '';
    const reference = filters.reference || '';
    const extraBaselineKey = useMemo(() => JSON.stringify(extraBaseline || []), [extraBaseline]);
    const inclKey = useMemo(() => stableSortedKey(include), [include]);
    const grpKey = useMemo(() => stableSortedKey(groupBy), [groupBy]);
    const customKey = useMemo(() => JSON.stringify(customBuckets || null), [customBuckets]);

    const fetchDashboard = useCallback(async () => {
        const myId = ++reqIdRef.current;
        setLoading(true);
        setError(null);
        try {
            const filterArgs = {
                fromDate: filters.fromDate,
                toDate: filters.toDate,
                courier: courierProzoId,
                city,
                state,
                awb,
                clientOrderId,
                missCategory,
                documents,
                attempts,
                reference: filters.reference || '',
            };
            const baselineAnd = [...buildBaselineAndFilter(filterArgs, merchantId, appointmentRequired), ...extraBaseline];
            const baselineOr = buildBaselineOrFilter(filterArgs);

            const wantBuckets = include.includes('buckets');
            const buckets = !wantBuckets ? undefined
                : customBuckets || BUCKET_KEYS.map((k) => ({ name: k, andfilter: buildPillFilter(k) }));

            const summaryScope = (include.includes('summary') || include.includes('parties'))
                ? {
                    merchantOid: merchantId || undefined,
                    courierProzoId: courierProzoId || undefined,
                    city: city || undefined,
                    state: state || undefined,
                }
                : undefined;

            const body = {
                baseline: { andfilter: baselineAnd, orfilter: baselineOr },
                summaryScope,
                include,
                buckets,
                groupBy: groupBy.length ? groupBy : undefined,
                partiesPage: include.includes('parties') ? partiesPage : undefined,
                partiesSize: include.includes('parties') ? partiesSize : undefined,
                partiesSearch: include.includes('parties') && partiesSearch ? partiesSearch : undefined,
                partiesAbdFilter: include.includes('parties') && partiesAbdFilter ? partiesAbdFilter : undefined,
            };

            const res = await postGeneric(apiConstants.APPOINTMENT_DELIVERY_DASHBOARD, body);
            if (myId !== reqIdRef.current) return;
            const obj = res?.data?.responseObj || {};

            const bucketsMap = obj.buckets || {};
            const summary = obj.summary || {};
            const merged = {
                ...EMPTY_STATE,
                ...summary,
                buckets: bucketsMap,
                summary,
                perf: obj.perf || {},
                reasons: obj.reasons || [],
                parties: obj.parties || EMPTY_STATE.parties,
                total: bucketsMap[PILL_KEYS.ALL] ?? 0,
                active: bucketsMap[PILL_KEYS.ACTIVE] ?? 0,
                miss1: bucketsMap[PILL_KEYS.MISS_1] ?? 0,
                miss2: bucketsMap[PILL_KEYS.MISS_2] ?? 0,
                miss3plus: bucketsMap[PILL_KEYS.MISS_3_PLUS] ?? 0,
                needsScheduling: bucketsMap[PILL_KEYS.NEEDS_SCHEDULING] ?? 0,
                delivered: bucketsMap[PILL_KEYS.DELIVERED] ?? 0,
                prozoFault: bucketsMap[PILL_KEYS.PROZO_FAULT] ?? 0,
                nonProzo: bucketsMap[PILL_KEYS.NON_PROZO] ?? 0,
                eventuallyDelivered: bucketsMap[PILL_KEYS.EVENTUALLY_DELIVERED] ?? 0,
                prozoFaultPending: bucketsMap[PILL_KEYS.PROZO_FAULT_PENDING] ?? 0,
            };
            setData(merged);
        } catch (e) {
            if (myId !== reqIdRef.current) return;
            setError(e);
            setData(EMPTY_STATE);
        }
        if (myId === reqIdRef.current) setLoading(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [from, to, merchantId, courierProzoId, city, state, awb, clientOrderId, missCategory, documents, attempts, reference,
        extraBaselineKey, inclKey, grpKey, customKey, partiesPage, partiesSize, partiesSearch, partiesAbdFilter, reload, appointmentRequired]);

    useEffect(() => { fetchDashboard(); }, [fetchDashboard]);

    return { data, loading, error, refetch: fetchDashboard };
}
